'use client'

import { CheckCircleFilled24, LightBulb24 } from '@vinted/monochrome-icons'
import { Cell, Icon, Image, Spacer } from '@vinted/web-ui'
import classNames from 'classnames'
import { useEffect } from 'react'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAsset } from '@marketplace-web/shared/assets'
import {
  viewEvent,
  SellerInsightsPhotoRecommendationsType,
} from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-data'

import SellerInsightsPhotoThumbnail from './SellerInsightsItemThumbnail'

type Props = {
  status: SellerInsightsPhotoRecommendationsType
  itemsToImprove: Array<{
    itemId: number
    photoUrl: string
    accessibilityDescription: string
  }>
}

const SellerInsightsPhotoRecommendations = ({ status, itemsToImprove }: Props) => {
  const translate = useTranslate('profile.seller_insights.photo_recommendations')
  const asset = useAsset('/assets')
  const { track } = useTracking()
  const breakpoints = useBreakpoint()

  const profileReworkAbTest = useAbTest('profile_wardrobe_web_rework')
  const isProfileReworkTestOn = profileReworkAbTest?.variant === 'on' && !breakpoints.portables

  const thumbnailClasses = classNames({
    'grid-container--4': isProfileReworkTestOn,
    'grid-container--6': !isProfileReworkTestOn,
  })

  useEffect(() => {
    track(
      viewEvent({
        target: 'photo_tips',
        screen: 'current_user_profile',
        targetDetails: JSON.stringify({
          status,
        }),
      }),
    )
  }, [track, status])

  const renderTitle = () => {
    return status === SellerInsightsPhotoRecommendationsType.NeedsImprovement
      ? translate('header')
      : translate('empty_case.header')
  }

  const renderBody = () => {
    switch (status) {
      case SellerInsightsPhotoRecommendationsType.AllImproved:
        return translate('empty_case.body_all_good')
      case SellerInsightsPhotoRecommendationsType.NoItems:
        return translate('empty_case.body_empty')
      default:
        return translate('body')
    }
  }

  const renderPrefix = () => {
    return status === SellerInsightsPhotoRecommendationsType.AllImproved ? (
      <Icon name={CheckCircleFilled24} color={Icon.Color.Primary} />
    ) : (
      <Icon name={LightBulb24} color={Icon.Color.GreyscaleLevel2} />
    )
  }

  const renderThumbnails = () => {
    if (itemsToImprove)
      return (
        <>
          <Spacer size={Spacer.Size.XLarge} />
          <div className={thumbnailClasses}>
            {itemsToImprove.map(({ itemId, photoUrl, accessibilityDescription }) => (
              <SellerInsightsPhotoThumbnail
                key={`item-to-improve${itemId}`}
                photoUrl={photoUrl}
                accessibilityDescription={accessibilityDescription}
                itemId={itemId}
              />
            ))}
          </div>
        </>
      )

    return null
  }

  return (
    <>
      <Spacer size={Spacer.Size.X2Large} />
      <Cell
        title={renderTitle()}
        body={renderBody()}
        prefix={renderPrefix()}
        styling={Cell.Styling.Tight}
        testId="seller-insights-photo-tips"
      />
      {renderThumbnails()}
      {status === SellerInsightsPhotoRecommendationsType.AllImproved && (
        <>
          <Spacer size={Spacer.Size.X2Large} />
          <div className="u-flexbox u-align-items-center">
            <Image
              size={Image.Size.X4Large}
              src={asset('check-person-sitting.svg')}
              testId="all-improved-image"
            />
          </div>
        </>
      )}
    </>
  )
}

export default SellerInsightsPhotoRecommendations
